import { useState } from "react";
import { loginAdmin } from "../service/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import type { AxiosError } from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const Login = ({ onesignalId }: any) => {
    const navigate = useNavigate();
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [formValues, setFormValues] = useState({
        email: '',
        password: ''
    });

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formValues.email.trim()) {
            newErrors.email = "Email is required";
        }
        if (!formValues.password.trim()) {
            newErrors.password = "Password is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };

    const handleSubmit = async (event: any) => {
        event.preventDefault();
        if (!validateForm()) {
            return;
        }
        try {
            const body = {
                email: formValues.email.trim().toLowerCase(),
                password: formValues.password,
                onesignal_id: onesignalId,
            };
            const result = await loginAdmin(body);
            const payload = result?.data;
            if (!payload) {
                toast.error("Unexpected response from server");
                return;
            }
            if (payload.status === false) {
                toast.error(payload.message ?? "Login failed");
            } else {
                toast.success(payload.message ?? "Logged in");
                if (payload.data != null) {
                    localStorage.setItem("admindetails", JSON.stringify(payload.data));
                }
                if (payload.token != null) {
                    localStorage.setItem("token", payload.token);
                }
                navigate('/');
            }
        } catch (err: any) {
            const axiosError = err as AxiosError<{ message?: string }>;
            const status = axiosError.response?.status;
            const serverMessage = axiosError.response?.data?.message;
            if (status === 401) {
                toast.error(serverMessage ?? "Invalid email or password.");
                return;
            }
            if (status === 403) {
                toast.error(serverMessage ?? "Admin password is not configured.");
                return;
            }
            toast.error(serverMessage ?? "Network error — please try again");
        }
    }

    return (
        <div className="h-screen flex items-center justify-center bg-background px-4">
            <Card className="w-full max-w-2xl shadow-xl">
                <CardContent className="p-0">
                    <div className="flex flex-row">
                        {/* Left Side: Image */}
                        <div className="w-1/3 flex items-center justify-center p-4">
                            <img
                                src="logo.png"
                                alt="Login Illustration"
                                className="w-full h-[350px] object-cover rounded-xl"
                            />
                        </div>

                        {/* Right Side: Content */}
                        <div className="flex-1 flex flex-col justify-center p-8">
                            <h2 className="text-2xl font-bold text-center text-foreground mb-6">
                                Welcome to EasyTurf
                            </h2>
                            <div className="space-y-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        name="email"
                                        placeholder="Enter Email"
                                        value={formValues.email}
                                        onChange={handleInputChange}
                                    />
                                    {errors.email && (
                                        <p className="text-destructive text-xs">{errors.email}</p>
                                    )}
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="password">Password</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        name="password"
                                        placeholder="Enter Password"
                                        value={formValues.password}
                                        onChange={handleInputChange}
                                    />
                                    {errors.password && (
                                        <p className="text-destructive text-xs">{errors.password}</p>
                                    )}
                                </div>
                                <div className="pt-2">
                                    <Button
                                        onClick={handleSubmit}
                                        className="w-full text-white hover:bg-gray-800"
                                        style={{
                                            background: 'linear-gradient(118deg, rgba(0,0,0,1) 0%, rgba(147,7,0,1) 100%)'
                                        }}
                                    >
                                        Login
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default Login;
